import { type BeamstarSessionDetail, type BeamstarStep, type BeamstarTurn } from '@consigli/types';

import { turnOutcomeMapping, workflowName } from '../format';

/** One thing the session reader shows: the user's message, a step, an error or the reply. */
interface Line {
  session: BeamstarSessionDetail;
  turn: BeamstarTurn;
  kind: string;
  agent?: string | null;
  tool?: string | null;
  result?: string | null;
  input?: string | null;
  output?: string | null;
  durationMs?: number | null;
}

const isReply = (line: Line) => line.kind === 'Reply';

const escapeCsvCell = (value: string | number | null | undefined) =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

// The reply closes its turn, so only it carries the turn's totals, and summing a token column
// counts each turn once.
const columns: { header: string; value: (line: Line) => string | number | null | undefined }[] = [
  { header: 'Conversation ID', value: ({ session }) => session.conversationId },
  { header: 'Project ID', value: ({ session }) => session.projectId },
  { header: 'Building ID', value: ({ session }) => session.buildingId },
  { header: 'Turn', value: ({ turn }) => turn.seq },
  { header: 'Turn started', value: ({ turn }) => turn.startedAt },
  {
    header: 'Workflow',
    value: ({ turn }) => (turn.workflowId ? workflowName(turn.workflowId) : null),
  },
  { header: 'Outcome', value: ({ turn }) => turnOutcomeMapping[turn.outcome].text },
  { header: 'Kind', value: (line) => line.kind },
  { header: 'Agent', value: (line) => line.agent },
  { header: 'Tool', value: (line) => line.tool },
  { header: 'Result', value: (line) => line.result },
  { header: 'Input', value: (line) => line.input },
  { header: 'Output', value: (line) => line.output },
  { header: 'Duration (ms)', value: (line) => line.durationMs },
  { header: 'Input tokens', value: (line) => (isReply(line) ? line.turn.inputTokens : null) },
  {
    header: 'Cached input tokens',
    value: (line) => (isReply(line) ? line.turn.cachedInputTokens : null),
  },
  { header: 'Output tokens', value: (line) => (isReply(line) ? line.turn.outputTokens : null) },
  { header: 'Flag comment', value: (line) => (isReply(line) ? line.turn.flagComment : null) },
];

const STEP_KINDS: Record<string, string> = {
  tool: 'Tool call',
  thinking: 'Thinking',
  text: 'Text',
};

const toolResult = (step: BeamstarStep) => {
  if (step.kind !== 'tool') {
    return null;
  }
  if (step.ok === null) {
    return 'No result';
  }
  return step.ok ? 'OK' : 'Failed';
};

const turnLines = (session: BeamstarSessionDetail, turn: BeamstarTurn): Line[] => [
  { session, turn, kind: 'Message', input: turn.message },
  ...turn.steps.map((step) => ({
    session,
    turn,
    kind: STEP_KINDS[step.kind] ?? step.kind,
    agent: step.agent,
    tool: step.tool,
    result: toolResult(step),
    input: step.arguments,
    output: step.output,
    durationMs: step.durationMs,
  })),
  ...(turn.error === null ? [] : [{ session, turn, kind: 'Error', output: turn.error }]),
  { session, turn, kind: 'Reply', output: turn.reply, durationMs: turn.durationMs },
];

/** One row per message, step, error and reply, in the order the session reader shows them. */
export const buildTranscriptCsv = (sessions: BeamstarSessionDetail[]) => {
  const lines = sessions.flatMap((session) =>
    session.turns.flatMap((turn) => turnLines(session, turn)),
  );
  const header = columns.map((column) => escapeCsvCell(column.header)).join(',');
  const rows = lines.map((line) =>
    columns.map((column) => escapeCsvCell(column.value(line))).join(','),
  );
  // The byte order mark makes Excel read the file as UTF-8.
  return `﻿${[header, ...rows].join('\n')}`;
};
